package io.jsonize.web.view

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Restore
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import io.jsonize.web.components.TooltipIconButton
import io.jsonize.web.screens.editor.CodeMirrorTextArea
import io.jsonize.web.screens.editor.ViewModeButton
import io.jsonize.web.screens.editor.java as javaLang
import io.jsonize.web.screens.editor.javascript as javascriptLang
import io.jsonize.web.services.copyToClipboard
import io.jsonize.web.services.downloadTextFile
import io.jsonize.web.services.logButtonClick
import io.jsonize.web.theme.LocalThemeIsDark
import io.jsonize.web.viewmodel.AppViewModel
import jsonize.sharedui.generated.resources.Res
import jsonize.sharedui.generated.resources.kotlin_svgrepo_com
import jsonize.sharedui.generated.resources.swift_svgrepo_com
import jsonize.sharedui.generated.resources.typescript_svgrepo_com
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.longOrNull
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.painterResource

enum class GenLang(val label: String, val extension: String) {
    Kotlin("Kotlin", "kt"),
    TypeScript("TypeScript", "ts"),
    Swift("Swift", "swift")
}

data class GenOptions(
    val nullable: Boolean,
    val defaultValues: Boolean
)

@Composable
fun GeneratorPanel(
    appViewModel: AppViewModel,
    inputJson: String,
    modifier: Modifier = Modifier
) {
    val showSettings by appViewModel.showCodeGeneratorSettings.collectAsState()

    var language by remember { mutableStateOf(GenLang.Kotlin) }
    var rootName by rememberSaveable { mutableStateOf("Root") }
    var options by remember { mutableStateOf(GenOptions(nullable = true, defaultValues = false)) }
    var editCopyEnabled by rememberSaveable { mutableStateOf(false) }
    var editableCode by rememberSaveable { mutableStateOf("") }
    var wasEditCopyEnabled by remember { mutableStateOf(false) }
    var actionsMenuExpanded by remember { mutableStateOf(false) }


    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val isDark by LocalThemeIsDark.current
    val languageExtension = remember(language) { generatorLanguageExtension(language) }

    val safeRootName = remember(rootName) { rootName.trim().ifEmpty { "Root" } }
    val generatedCode by remember(inputJson, language, safeRootName, options) {
        derivedStateOf {
            generateModels(inputJson, language, safeRootName, options)
        }
    }

    LaunchedEffect(editCopyEnabled) {
        if (editCopyEnabled && !wasEditCopyEnabled) {
            editableCode = generatedCode
        } else if (!editCopyEnabled && wasEditCopyEnabled) {
            editableCode = ""
        }
        wasEditCopyEnabled = editCopyEnabled
    }

    val codeToUse = if (editCopyEnabled) editableCode else generatedCode

    LaunchedEffect(inputJson, language, codeToUse, safeRootName, options){
        appViewModel.updateCode(codeToUse, language.filenameFor(safeRootName))
    }

   /* fun handleCopy() {
        scope.launch {
            val ok = copyToClipboard(codeToUse)
            if (!ok) {
                snackbarHostState.showSnackbar("Copy not supported")
            }
        }
    }*/

    /*fun handleDownload() {
        scope.launch {
            val filename = language.filenameFor(safeRootName)
            val savedPath = downloadTextFile(filename, codeToUse)
            if (!savedPath.isNullOrBlank()) {
                snackbarHostState.showSnackbar("Saved to $savedPath")
            }
        }
    }*/

    Column(modifier = modifier.fillMaxSize()) {
        if (showSettings) {
            Surface(tonalElevation = 1.dp) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Text("Language")
                        LanguageIconButton(
                            label = "Kotlin",
                            icon = Res.drawable.kotlin_svgrepo_com,
                            selected = language == GenLang.Kotlin,
                            onClick = { language = GenLang.Kotlin }
                        )
                        LanguageIconButton(
                            label = "TypeScript",
                            icon = Res.drawable.typescript_svgrepo_com,
                            selected = language == GenLang.TypeScript,
                            onClick = { language = GenLang.TypeScript }
                        )
                        LanguageIconButton(
                            label = "Swift",
                            icon = Res.drawable.swift_svgrepo_com,
                            selected = language == GenLang.Swift,
                            onClick = { language = GenLang.Swift }
                        )
                    }

                    OutlinedTextField(
                        value = rootName,
                        onValueChange = { rootName = it },
                        label = { Text("Root name") },
                        singleLine = true,
                        modifier = Modifier
                    )

                    Column(Modifier.padding(top = 6.dp)) {
                        Text("Options", style = MaterialTheme.typography.labelMedium)
                        FlowRow(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(
                                    checked = options.nullable,
                                    onCheckedChange = { options = options.copy(nullable = it) }
                                )
                                Text("Nullable")
                                /*Spacer(Modifier.width(6.dp))
                            Switch(
                                checked = options.nullable,
                                onCheckedChange = { options = options.copy(nullable = it) }
                            )*/
                            }
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(
                                    checked = options.defaultValues,
                                    onCheckedChange = { options = options.copy(defaultValues = it) }
                                )
                                Text("Default values")
                                /*Spacer(Modifier.width(6.dp))
                            Switch(
                                checked = options.defaultValues,
                                onCheckedChange = { options = options.copy(defaultValues = it) }
                            )*/
                            }

                            /*Box {
                        IconButton(onClick = { actionsMenuExpanded = true }) {
                            Icon(Icons.Default.MoreVert, contentDescription = "Tools")
                        }
                        DropdownMenu(
                            expanded = actionsMenuExpanded,
                            onDismissRequest = { actionsMenuExpanded = false }
                        ) {
                            DropdownMenuItem(
                                text = { Text("Copy") },
                                leadingIcon = { Icon(Icons.Default.ContentCopy, contentDescription = null) },
                                onClick = {
                                    actionsMenuExpanded = false
                                    handleCopy()
                                }
                            )
                            DropdownMenuItem(
                                text = { Text("Download") },
                                leadingIcon = { Icon(Icons.Default.Download, contentDescription = null) },
                                onClick = {
                                    actionsMenuExpanded = false
                                    handleDownload()
                                }
                            )
                        }
                    }*/
                        }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(top = 6.dp)
                        ) {
                            Text("Edit Generated Code")
                            Spacer(Modifier.width(6.dp))
                            Switch(
                                checked = editCopyEnabled,
                                onCheckedChange = { editCopyEnabled = it }
                            )
//                        Spacer(Modifier.weight(1f))
                    if (editCopyEnabled) {
                        TextButton(
                            onClick = {
                                logButtonClick("Reset to Generated")
                                editableCode = generatedCode
                            },
                            contentPadding = ButtonDefaults.TextButtonContentPadding
                        ) {
                                    Icon(Icons.Default.Restore, contentDescription = null)
                                    Spacer(Modifier.width(6.dp))
                                    Text("Reset")
                                }
                            }
                        }

                    }
                }
            }

            HorizontalDivider()
        }

        Box(modifier = Modifier.fillMaxSize()) {
            Column(Modifier.fillMaxSize().padding(top = 12.dp, start = 6.dp, end = 6.dp, bottom = 6.dp)) {
                Text("Generated Code", style = MaterialTheme.typography.labelLarge)
                Spacer(Modifier.height(6.dp))
                Surface(
                    tonalElevation = 1.dp,
                    shape = MaterialTheme.shapes.medium,
                    modifier = Modifier.weight(1f).fillMaxWidth()
                ) {
                    val codeValue = if (editCopyEnabled) editableCode else generatedCode
                    CodeMirrorTextArea(
                        value = codeValue,
                        onValueChange = { updated ->
                            if (editCopyEnabled) editableCode = updated
                        },
                        isEditable = editCopyEnabled,
                        isDark = isDark,
                        languageExtension = languageExtension,
                        placeholderText = "Generated code will appear here.",
                        modifier = Modifier.fillMaxSize()
                    )
                }
            }

            SnackbarHost(
                hostState = snackbarHostState,
                modifier = Modifier.align(Alignment.BottomEnd).padding(12.dp)
            )
        }
    }
}

@Composable
fun ColumnScope.GeneratorToolbar(
    appViewModel: AppViewModel,
    copy: () -> Unit,
    download: () -> Unit,
    modifier: Modifier = Modifier){

    val showSettings by appViewModel.showCodeGeneratorSettings.collectAsState()

    ViewModeButton(
        showSettings,
        onClick = { appViewModel.updateShowSettings(!showSettings) },
        icon = Icons.Default.Settings,
        description = "Code Generator Settings"
    )

    /*ToggleIconButton(
        Icons.Default.Settings,
        showSettings,
        onClick = { appViewModel.updateShowSettings(!showSettings) },
    )*/

    TooltipIconButton(
        tooltip = "Copy",
        onClick = copy
    ) {
        Icon(
            imageVector = Icons.Default.ContentCopy,
            contentDescription = "Copy"
        )
    }

    TooltipIconButton(
        tooltip = "Download",
        onClick = download
    ) {
        Icon(
            imageVector = Icons.Default.Download,
            contentDescription = "Download"
        )
    }
}

fun generateModels(
    inputJson: String,
    language: GenLang,
    rootName: String,
    opts: GenOptions
): String {
    if (inputJson.isBlank()) return ""
    val parsed = try {
        jsonParser.parseToJsonElement(inputJson)
    } catch (e: Exception) {
        val message = e.message?.trim().orEmpty()
        val detail = if (message.isBlank()) "Invalid JSON" else "Invalid JSON: $message"
        return "// $detail"
    }
    val safeRoot = rootName.trim().ifEmpty { "Root" }
    val rootType = inferType(parsed, safeRoot).type
    return when (language) {
        GenLang.Kotlin -> renderKotlinModels(rootType, safeRoot, opts)
        GenLang.TypeScript -> renderTypeScriptModels(rootType, safeRoot, opts)
        GenLang.Swift -> renderSwiftModels(rootType, safeRoot, opts)
    }
}

private fun GenLang.filenameFor(rootName: String): String {
    val safe = rootName.trim().ifEmpty { "Root" }
    return when (this) {
        GenLang.Kotlin -> "$safe.${extension}"
        GenLang.TypeScript -> "$safe.${extension}"
        GenLang.Swift -> "$safe.${extension}"
    }
}

private val jsonParser = Json { isLenient = true }

private sealed class SchemaType {
    data class ObjectType(val nameHint: String?, val fields: LinkedHashMap<String, FieldSchema>) : SchemaType()
    data class ArrayType(val element: TypeRef) : SchemaType()
    data class PrimitiveType(val kind: PrimitiveKind) : SchemaType()
    data class UnionType(val kinds: Set<UnionKind>) : SchemaType()
    object AnyType : SchemaType()
    object NullType : SchemaType()
}

private data class TypeRef(val type: SchemaType, val nullable: Boolean)

private data class FieldSchema(val type: TypeRef, val missing: Boolean)

private enum class PrimitiveKind {
    String,
    Int,
    Double,
    Boolean
}

private enum class UnionKind {
    String,
    Number,
    Boolean
}

private fun inferType(element: JsonElement, nameHint: String?): TypeRef {
    return when (element) {
        is JsonNull -> TypeRef(SchemaType.NullType, nullable = true)
        is JsonPrimitive -> inferPrimitiveType(element)
        is JsonArray -> inferArrayType(element, nameHint)
        is JsonObject -> inferObjectType(element, nameHint)
        else -> TypeRef(SchemaType.AnyType, nullable = false)
    }
}

private fun inferPrimitiveType(primitive: JsonPrimitive): TypeRef {
    if (primitive.isString) {
        return TypeRef(SchemaType.PrimitiveType(PrimitiveKind.String), nullable = false)
    }
    val boolean = primitive.booleanOrNull
    if (boolean != null) {
        return TypeRef(SchemaType.PrimitiveType(PrimitiveKind.Boolean), nullable = false)
    }
    val content = primitive.content
    val longValue = primitive.longOrNull
    val isInteger = longValue != null &&
        longValue in Int.MIN_VALUE..Int.MAX_VALUE &&
        !content.contains('.') &&
        !content.contains('e', ignoreCase = true)
    val kind = if (isInteger) PrimitiveKind.Int else PrimitiveKind.Double
    return TypeRef(SchemaType.PrimitiveType(kind), nullable = false)
}

private fun inferArrayType(array: JsonArray, nameHint: String?): TypeRef {
    if (array.isEmpty()) {
        return TypeRef(SchemaType.ArrayType(TypeRef(SchemaType.AnyType, nullable = false)), nullable = false)
    }
    val elementHint = nameHint?.let { singularize(it) }
    var merged: TypeRef? = null
    for (item in array) {
        val itemType = inferType(item, elementHint)
        merged = if (merged == null) itemType else mergeTypeRefs(merged, itemType)
    }
    val elementType = merged ?: TypeRef(SchemaType.AnyType, nullable = false)
    return TypeRef(SchemaType.ArrayType(elementType), nullable = false)
}

private fun inferObjectType(obj: JsonObject, nameHint: String?): TypeRef {
    val fields = LinkedHashMap<String, FieldSchema>()
    for ((key, value) in obj) {
        fields[key] = FieldSchema(inferType(value, key), missing = false)
    }
    return TypeRef(SchemaType.ObjectType(nameHint, fields), nullable = false)
}

private fun mergeTypeRefs(left: TypeRef, right: TypeRef): TypeRef {
    val nullable = left.nullable || right.nullable
    val leftType = left.type
    val rightType = right.type
    if (leftType is SchemaType.NullType) {
        return TypeRef(rightType, nullable = true)
    }
    if (rightType is SchemaType.NullType) {
        return TypeRef(leftType, nullable = true)
    }
    val mergedType = mergeTypes(leftType, rightType)
    return TypeRef(mergedType, nullable = nullable)
}

private fun mergeTypes(left: SchemaType, right: SchemaType): SchemaType {
    if (left is SchemaType.AnyType || right is SchemaType.AnyType) return SchemaType.AnyType
    if ((left is SchemaType.PrimitiveType || left is SchemaType.UnionType) &&
        (right is SchemaType.PrimitiveType || right is SchemaType.UnionType)
    ) {
        return mergePrimitiveTypes(left, right)
    }
    if (left is SchemaType.ArrayType && right is SchemaType.ArrayType) {
        return SchemaType.ArrayType(mergeTypeRefs(left.element, right.element))
    }
    if (left is SchemaType.ObjectType && right is SchemaType.ObjectType) {
        return mergeObjectTypes(left, right)
    }
    return SchemaType.AnyType
}

private fun mergePrimitiveTypes(left: SchemaType, right: SchemaType): SchemaType {
    val leftKinds = toUnionKinds(left)
    val rightKinds = toUnionKinds(right)
    val mergedKinds = (leftKinds + rightKinds).toSet()
    if (mergedKinds.size == 1 && mergedKinds.contains(UnionKind.Number)) {
        val numberKind = mergeNumberKind(left, right)
        return SchemaType.PrimitiveType(numberKind)
    }
    if (mergedKinds.size == 1 && mergedKinds.contains(UnionKind.String)) {
        return SchemaType.PrimitiveType(PrimitiveKind.String)
    }
    if (mergedKinds.size == 1 && mergedKinds.contains(UnionKind.Boolean)) {
        return SchemaType.PrimitiveType(PrimitiveKind.Boolean)
    }
    return SchemaType.UnionType(mergedKinds)
}

private fun mergeNumberKind(left: SchemaType, right: SchemaType): PrimitiveKind {
    val leftKind = extractNumberKind(left)
    val rightKind = extractNumberKind(right)
    return if (leftKind == PrimitiveKind.Double || rightKind == PrimitiveKind.Double) {
        PrimitiveKind.Double
    } else {
        PrimitiveKind.Int
    }
}

private fun extractNumberKind(type: SchemaType): PrimitiveKind? {
    return when (type) {
        is SchemaType.PrimitiveType -> when (type.kind) {
            PrimitiveKind.Int, PrimitiveKind.Double -> type.kind
            else -> null
        }
        is SchemaType.UnionType -> if (type.kinds.size == 1 && type.kinds.contains(UnionKind.Number)) {
            PrimitiveKind.Double
        } else {
            null
        }
        else -> null
    }
}

private fun toUnionKinds(type: SchemaType): Set<UnionKind> {
    return when (type) {
        is SchemaType.PrimitiveType -> when (type.kind) {
            PrimitiveKind.String -> setOf(UnionKind.String)
            PrimitiveKind.Boolean -> setOf(UnionKind.Boolean)
            PrimitiveKind.Int, PrimitiveKind.Double -> setOf(UnionKind.Number)
        }
        is SchemaType.UnionType -> type.kinds
        else -> emptySet()
    }
}

private fun mergeObjectTypes(left: SchemaType.ObjectType, right: SchemaType.ObjectType): SchemaType.ObjectType {
    val fields = LinkedHashMap<String, FieldSchema>()
    val keys = LinkedHashSet<String>()
    keys.addAll(left.fields.keys)
    keys.addAll(right.fields.keys)
    for (key in keys) {
        val leftField = left.fields[key]
        val rightField = right.fields[key]
        val merged = when {
            leftField != null && rightField != null -> {
                FieldSchema(
                    type = mergeTypeRefs(leftField.type, rightField.type),
                    missing = leftField.missing || rightField.missing
                )
            }
            leftField != null -> leftField.copy(missing = true)
            rightField != null -> rightField.copy(missing = true)
            else -> null
        }
        if (merged != null) {
            fields[key] = merged
        }
    }
    val nameHint = left.nameHint ?: right.nameHint
    return SchemaType.ObjectType(nameHint, fields)
}

private fun renderKotlinModels(rootType: SchemaType, rootName: String, opts: GenOptions): String {
    val nameMap = buildNameMap(rootType, rootName, GenLang.Kotlin)
    val order = collectObjectOrder(rootType)
    return buildString {
        if (order.isNotEmpty()) {
            appendLine("import kotlinx.serialization.SerialName")
            appendLine("import kotlinx.serialization.Serializable")
            appendLine()
        }
        if (rootType !is SchemaType.ObjectType) {
            val typeRef = TypeRef(rootType, nullable = false)
            appendLine("typealias ${safeTypeName(rootName)} = ${renderKotlinType(typeRef, nameMap, opts.nullable)}")
            if (order.isNotEmpty()) appendLine()
        }
        val includeNulls = opts.nullable
        for ((index, obj) in order.withIndex()) {
            val className = nameMap[obj] ?: safeTypeName(obj.nameHint ?: "Model")
            appendLine("@Serializable")
            appendLine("data class $className(")
            if (obj.fields.isNotEmpty()) {
                val propInfo = buildKotlinPropertyNames(obj.fields.keys)
                val lastIndex = obj.fields.size - 1
                obj.fields.entries.forEachIndexed { fieldIndex, entry ->
                    val (rawName, field) = entry
                    val info = propInfo.getValue(rawName)
                    val fieldNullable = includeNulls && (field.missing || field.type.nullable)
                    val typeRef = field.type.copy(nullable = fieldNullable)
                    val typeName = renderKotlinType(typeRef, nameMap, includeNulls)
                    val defaultValue = kotlinDefaultValue(typeRef, includeNulls, opts.defaultValues)
                    if (info.needsSerialName) {
                        appendLine("    @SerialName(\"${escapeKotlinString(rawName)}\")")
                    }
                    val comma = if (fieldIndex == lastIndex) "" else ","
                    val defaultSuffix = if (defaultValue != null) " = $defaultValue" else ""
                    appendLine("    val ${info.name}: $typeName$defaultSuffix$comma")
                }
            }
            append(")")
            appendLine()
            if (index != order.lastIndex) appendLine()
        }
    }
}

private fun renderTypeScriptModels(rootType: SchemaType, rootName: String, opts: GenOptions): String {
    val nameMap = buildNameMap(rootType, rootName, GenLang.TypeScript)
    val order = collectObjectOrder(rootType)
    return buildString {
        if (rootType !is SchemaType.ObjectType) {
            val rootAlias = safeTypeName(rootName)
            val typeRef = TypeRef(rootType, nullable = false)
            appendLine("export type $rootAlias = ${renderTypeScriptType(typeRef, nameMap, opts.nullable)};")
            if (order.isNotEmpty()) appendLine()
        }
        for ((index, obj) in order.withIndex()) {
            val name = nameMap[obj] ?: safeTypeName(obj.nameHint ?: "Model")
            appendLine("export interface $name {")
            if (obj.fields.isNotEmpty()) {
                obj.fields.entries.forEach { (rawName, field) ->
                    val optional = opts.nullable && (field.missing || field.type.nullable)
                    val typeRef = field.type.copy(nullable = opts.nullable && field.type.nullable)
                    val typeName = renderTypeScriptType(typeRef, nameMap, opts.nullable)
                    val key = renderTypeScriptKey(rawName)
                    val suffix = if (optional) "?" else ""
                    appendLine("  $key$suffix: $typeName;")
                }
            }
            appendLine("}")
            if (index != order.lastIndex) appendLine()
        }
    }
}

private fun renderSwiftModels(rootType: SchemaType, rootName: String, opts: GenOptions): String {
    val nameMap = buildNameMap(rootType, rootName, GenLang.Swift)
    val order = collectObjectOrder(rootType)
    return buildString {
        if (rootType !is SchemaType.ObjectType) {
            val rootAlias = safeTypeName(rootName)
            val typeRef = TypeRef(rootType, nullable = false)
            appendLine("typealias $rootAlias = ${renderSwiftType(typeRef, nameMap, opts.nullable)}")
            if (order.isNotEmpty()) appendLine()
        }
        for ((index, obj) in order.withIndex()) {
            val name = nameMap[obj] ?: safeTypeName(obj.nameHint ?: "Model")
            appendLine("struct $name: Codable {")
            val propInfo = buildSwiftPropertyNames(obj.fields.keys)
            obj.fields.entries.forEach { (rawName, field) ->
                val info = propInfo.getValue(rawName)
                val fieldNullable = opts.nullable && (field.missing || field.type.nullable)
                val typeRef = field.type.copy(nullable = fieldNullable)
                val typeName = renderSwiftType(typeRef, nameMap, opts.nullable)
                val defaultValue = swiftDefaultValue(typeRef, opts.nullable, opts.defaultValues)
                val defaultSuffix = if (defaultValue != null) " = $defaultValue" else ""
                appendLine("  let ${info.name}: $typeName$defaultSuffix")
            }
            val codingKeys = propInfo.filter { it.value.needsCodingKey }
            if (codingKeys.isNotEmpty()) {
                appendLine()
                appendLine("  enum CodingKeys: String, CodingKey {")
                codingKeys.forEach { (rawName, info) ->
                    appendLine("    case ${info.name} = \"${escapeSwiftString(rawName)}\"")
                }
                appendLine("  }")
            }
            appendLine("}")
            if (index != order.lastIndex) appendLine()
        }
    }
}

private fun collectObjectOrder(rootType: SchemaType): List<SchemaType.ObjectType> {
    val order = mutableListOf<SchemaType.ObjectType>()
    val seen = mutableSetOf<SchemaType.ObjectType>()
    fun visit(type: SchemaType) {
        when (type) {
            is SchemaType.ObjectType -> {
                if (seen.add(type)) {
                    order.add(type)
                    for (field in type.fields.values) {
                        visit(field.type.type)
                    }
                }
            }
            is SchemaType.ArrayType -> visit(type.element.type)
            else -> Unit
        }
    }
    visit(rootType)
    return order
}

private fun buildNameMap(rootType: SchemaType, rootName: String, language: GenLang): Map<SchemaType.ObjectType, String> {
    val allocator = NameAllocator()
    val nameMap = mutableMapOf<SchemaType.ObjectType, String>()
    fun allocateName(suggestion: String?): String {
        val base = safeTypeName(suggestion ?: "Model")
        val safe = safeTypeNameForLanguage(base, language)
        return allocator.allocate(safe)
    }
    fun visit(type: SchemaType, suggestion: String?) {
        when (type) {
            is SchemaType.ObjectType -> {
                if (!nameMap.containsKey(type)) {
                    val baseName = suggestion ?: type.nameHint ?: "Model"
                    nameMap[type] = allocateName(baseName)
                }
                for ((fieldName, field) in type.fields) {
                    val fieldType = field.type.type
                    val childSuggestion = when (fieldType) {
                        is SchemaType.ObjectType -> fieldName
                        is SchemaType.ArrayType -> if (fieldType.element.type is SchemaType.ObjectType) {
                            singularize(fieldName)
                        } else {
                            null
                        }
                        else -> null
                    }
                    if (fieldType is SchemaType.ArrayType) {
                        visit(fieldType.element.type, childSuggestion)
                    } else {
                        visit(fieldType, childSuggestion)
                    }
                }
            }
            is SchemaType.ArrayType -> {
                visit(type.element.type, suggestion)
            }
            else -> Unit
        }
    }
    val rootSuggestion = if (rootType is SchemaType.ObjectType) rootName else singularize(rootName)
    visit(rootType, rootSuggestion)
    return nameMap
}

private data class KotlinPropInfo(val name: String, val needsSerialName: Boolean)

private fun buildKotlinPropertyNames(keys: Set<String>): Map<String, KotlinPropInfo> {
    val allocator = NameAllocator()
    val result = LinkedHashMap<String, KotlinPropInfo>()
    for (key in keys) {
        val words = splitWords(key)
        var name = if (words.isEmpty()) "field" else toCamelCase(words)
        if (name.firstOrNull()?.isDigit() == true) {
            name = "n$name"
        }
        if (kotlinReserved.contains(name)) {
            name += "Value"
        }
        name = allocator.allocate(name)
        result[key] = KotlinPropInfo(name = name, needsSerialName = name != key)
    }
    return result
}

private data class SwiftPropInfo(val name: String, val needsCodingKey: Boolean)

private fun buildSwiftPropertyNames(keys: Set<String>): Map<String, SwiftPropInfo> {
    val allocator = NameAllocator()
    val result = LinkedHashMap<String, SwiftPropInfo>()
    for (key in keys) {
        val words = splitWords(key)
        var name = if (words.isEmpty()) "field" else toCamelCase(words)
        if (name.firstOrNull()?.isDigit() == true) {
            name = "n$name"
        }
        if (swiftReserved.contains(name)) {
            name += "Value"
        }
        name = allocator.allocate(name)
        result[key] = SwiftPropInfo(name = name, needsCodingKey = name != key)
    }
    return result
}

private fun renderKotlinType(typeRef: TypeRef, nameMap: Map<SchemaType.ObjectType, String>, includeNulls: Boolean): String {
    val base = when (val type = typeRef.type) {
        is SchemaType.ObjectType -> nameMap[type] ?: safeTypeName(type.nameHint ?: "Model")
        is SchemaType.ArrayType -> "List<${renderKotlinType(type.element, nameMap, includeNulls)}>"
        is SchemaType.PrimitiveType -> when (type.kind) {
            PrimitiveKind.String -> "String"
            PrimitiveKind.Int -> "Int"
            PrimitiveKind.Double -> "Double"
            PrimitiveKind.Boolean -> "Boolean"
        }
        is SchemaType.UnionType -> "Any"
        is SchemaType.AnyType -> "Any"
        is SchemaType.NullType -> "Any"
    }
    val nullable = includeNulls && typeRef.nullable
    return if (nullable) "$base?" else base
}

private fun renderTypeScriptType(typeRef: TypeRef, nameMap: Map<SchemaType.ObjectType, String>, includeNulls: Boolean): String {
    val base = when (val type = typeRef.type) {
        is SchemaType.ObjectType -> nameMap[type] ?: safeTypeName(type.nameHint ?: "Model")
        is SchemaType.ArrayType -> {
            val element = renderTypeScriptType(type.element, nameMap, includeNulls)
            val wrapped = if (element.contains(" | ")) "($element)" else element
            "$wrapped[]"
        }
        is SchemaType.PrimitiveType -> when (type.kind) {
            PrimitiveKind.String -> "string"
            PrimitiveKind.Int, PrimitiveKind.Double -> "number"
            PrimitiveKind.Boolean -> "boolean"
        }
        is SchemaType.UnionType -> type.kinds.joinToString(" | ") { kind ->
            when (kind) {
                UnionKind.String -> "string"
                UnionKind.Number -> "number"
                UnionKind.Boolean -> "boolean"
            }
        }
        is SchemaType.AnyType -> "any"
        is SchemaType.NullType -> "any"
    }
    return if (includeNulls && typeRef.nullable) "$base | null" else base
}

private fun renderSwiftType(typeRef: TypeRef, nameMap: Map<SchemaType.ObjectType, String>, includeNulls: Boolean): String {
    val base = when (val type = typeRef.type) {
        is SchemaType.ObjectType -> nameMap[type] ?: safeTypeName(type.nameHint ?: "Model")
        is SchemaType.ArrayType -> "[${renderSwiftType(type.element, nameMap, includeNulls)}]"
        is SchemaType.PrimitiveType -> when (type.kind) {
            PrimitiveKind.String -> "String"
            PrimitiveKind.Int -> "Int"
            PrimitiveKind.Double -> "Double"
            PrimitiveKind.Boolean -> "Bool"
        }
        is SchemaType.UnionType -> "Any"
        is SchemaType.AnyType -> "Any"
        is SchemaType.NullType -> "Any"
    }
    val nullable = includeNulls && typeRef.nullable
    return if (nullable) "$base?" else base
}

private fun renderTypeScriptKey(key: String): String {
    val valid = tsIdentifierRegex.matches(key) && !tsReserved.contains(key)
    return if (valid) key else "\"${escapeTypeScriptString(key)}\""
}

private fun kotlinDefaultValue(typeRef: TypeRef, includeNulls: Boolean, enabled: Boolean): String? {
    if (!enabled) return null
    if (includeNulls && typeRef.nullable) return "null"
    return when (typeRef.type) {
        is SchemaType.PrimitiveType -> when (typeRef.type.kind) {
            PrimitiveKind.String -> "\"\""
            PrimitiveKind.Int -> "0"
            PrimitiveKind.Double -> "0.0"
            PrimitiveKind.Boolean -> "false"
        }
        is SchemaType.ArrayType -> "emptyList()"
        else -> null
    }
}

private fun swiftDefaultValue(typeRef: TypeRef, includeNulls: Boolean, enabled: Boolean): String? {
    if (!enabled) return null
    if (includeNulls && typeRef.nullable) return "nil"
    return when (typeRef.type) {
        is SchemaType.PrimitiveType -> when (typeRef.type.kind) {
            PrimitiveKind.String -> "\"\""
            PrimitiveKind.Int -> "0"
            PrimitiveKind.Double -> "0"
            PrimitiveKind.Boolean -> "false"
        }
        is SchemaType.ArrayType -> "[]"
        else -> null
    }
}

private class NameAllocator {
    private val used = mutableSetOf<String>()

    fun allocate(base: String): String {
        var candidate = base
        var index = 2
        while (used.contains(candidate)) {
            candidate = "$base$index"
            index += 1
        }
        used.add(candidate)
        return candidate
    }
}

private val kotlinReserved = setOf(
    "as", "break", "class", "continue", "do", "else", "false", "for", "fun", "if", "in",
    "interface", "is", "null", "object", "package", "return", "super", "this", "throw", "true",
    "try", "typealias", "val", "var", "when", "while", "by", "catch", "constructor", "delegate",
    "dynamic", "field", "file", "finally", "get", "import", "init", "param", "property",
    "receiver", "set", "setparam", "where", "actual", "abstract", "annotation", "companion",
    "const", "crossinline", "data", "enum", "expect", "external", "final", "infix", "inline",
    "inner", "internal", "lateinit", "noinline", "open", "operator", "out", "override", "private",
    "protected", "public", "reified", "sealed", "suspend", "tailrec", "vararg"
)

private val swiftReserved = setOf(
    "associatedtype", "class", "deinit", "enum", "extension", "fileprivate", "func", "import",
    "init", "inout", "internal", "let", "open", "operator", "private", "protocol", "public",
    "static", "struct", "subscript", "typealias", "var", "break", "case", "continue", "default",
    "defer", "do", "else", "fallthrough", "for", "guard", "if", "in", "repeat", "return",
    "switch", "where", "while", "as", "any", "catch", "false", "is", "nil", "rethrows",
    "super", "self", "throw", "throws", "true", "try"
)

private val tsReserved = setOf(
    "break", "case", "catch", "class", "const", "continue", "debugger", "default", "delete", "do",
    "else", "enum", "export", "extends", "false", "finally", "for", "function", "if", "import",
    "in", "instanceof", "new", "null", "return", "super", "switch", "this", "throw", "true",
    "try", "typeof", "var", "void", "while", "with", "let", "static", "yield", "await",
    "implements", "interface", "package", "private", "protected", "public", "type", "of"
)

private val tsIdentifierRegex = Regex("^[A-Za-z_][A-Za-z0-9_]*$")

private fun splitWords(input: String): List<String> {
    if (input.isBlank()) return emptyList()
    val parts = input.trim().split(Regex("[_\\-\\s.]+"))
    return parts.mapNotNull { part ->
        val cleaned = part.filter { it.isLetterOrDigit() }
        cleaned.ifBlank { null }
    }
}

private fun toCamelCase(words: List<String>): String {
    if (words.isEmpty()) return ""
    val first = words.first().replaceFirstChar { it.lowercaseChar() }
    val rest = words.drop(1).joinToString("") { it.replaceFirstChar { ch -> ch.uppercaseChar() } }
    return first + rest
}

private fun toPascalCase(words: List<String>): String {
    return words.joinToString("") { it.replaceFirstChar { ch -> ch.uppercaseChar() } }
}

private fun safeTypeName(raw: String): String {
    val words = splitWords(raw)
    val base = if (words.isEmpty()) "Model" else toPascalCase(words)
    return if (base.firstOrNull()?.isDigit() == true) "N$base" else base.ifBlank { "Model" }
}

private fun safeTypeNameForLanguage(raw: String, language: GenLang): String {
    val base = safeTypeName(raw)
    return when (language) {
        GenLang.Kotlin -> if (kotlinReserved.contains(base)) "${base}Model" else base
        GenLang.TypeScript -> if (tsReserved.contains(base)) "${base}Model" else base
        GenLang.Swift -> if (swiftReserved.contains(base)) "${base}Model" else base
    }
}

private fun singularize(value: String): String {
    val trimmed = value.trim()
    if (trimmed.length <= 1) return trimmed
    return if (trimmed.endsWith("s", ignoreCase = true)) {
        trimmed.dropLast(1)
    } else {
        trimmed + "Item"
    }
}

private fun escapeKotlinString(value: String): String {
    return value
        .replace("\\", "\\\\")
        .replace("\"", "\\\"")
        .replace("\n", "\\n")
        .replace("\r", "\\r")
        .replace("\t", "\\t")
}

private fun escapeTypeScriptString(value: String): String {
    return value
        .replace("\\", "\\\\")
        .replace("\"", "\\\"")
        .replace("\n", "\\n")
        .replace("\r", "\\r")
        .replace("\t", "\\t")
}

private fun escapeSwiftString(value: String): String {
    return value
        .replace("\\", "\\\\")
        .replace("\"", "\\\"")
        .replace("\n", "\\n")
        .replace("\r", "\\r")
        .replace("\t", "\\t")
}

private fun generatorLanguageExtension(language: GenLang): Any? {
    return when (language) {
        GenLang.TypeScript -> {
            val config = js("({})")
            config.typescript = true
            javascriptLang(config)
        }
        GenLang.Kotlin -> javaLang()
        GenLang.Swift -> javaLang()
    }
}

@Composable
private fun ToggleIconButton(
    icon: ImageVector,
    selected: Boolean,
    onClick: () -> Unit
) {
    val background = if (selected) {
        MaterialTheme.colorScheme.primary
    } else {
        MaterialTheme.colorScheme.surfaceVariant
    }

    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(background)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Icon(icon, contentDescription = null)
    }
}

@Composable
@OptIn(ExperimentalResourceApi::class)
private fun LanguageIconButton(
    label: String,
    icon: DrawableResource,
    selected: Boolean,
    onClick: () -> Unit
) {
    val background = if (selected) {
        MaterialTheme.colorScheme.primary
    } else {
        MaterialTheme.colorScheme.surfaceVariant
    }

    TooltipIconButton(
        tooltip = label,
        onClick = onClick
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .clip(CircleShape)
                .background(background),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(icon),
                contentDescription = label,
                tint = Color.Unspecified,
                modifier = Modifier.size(18.dp)
            )
        }
    }
}
